import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { IPMAStation } from "@/types/ipma-station/ipma-station.types";
import { InformationSource } from "@/types/information-source/information-source.types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FirestoreService from "@/services/firestore";
import { useData } from "@/hooks/useData";

interface IPMAStationFormProps {
  basins: { id: string; name: string }[];
  isOpen: boolean;
  onClose: () => void;
  stationToEdit?: IPMAStation | null;
  onSuccess: () => void;
}

export const IPMAStationForm = ({ basins, isOpen, onClose, stationToEdit, onSuccess }: IPMAStationFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Get information sources for the dropdowns
  const { data: infoSources = [] } = useData<InformationSource>({
    collectionName: "setup_infoSources",
  });

  // Form state
  const [name, setName] = useState("");
  const [basinId, setBasinId] = useState("");
  const [precipitationRate, setPrecipitationRate] = useState<number | undefined>(undefined);
  const [precipitationSourceId, setPrecipitationSourceId] = useState("");
  const [evaporationRate, setEvaporationRate] = useState<number | undefined>(undefined);
  const [evaporationSourceId, setEvaporationSourceId] = useState("");

  // Validation state
  const [nameError, setNameError] = useState("");
  const [basinError, setBasinError] = useState("");
  const [precipitationRateError, setPrecipitationRateError] = useState("");
  const [precipitationSourceError, setPrecipitationSourceError] = useState("");
  const [evaporationRateError, setEvaporationRateError] = useState("");
  const [evaporationSourceError, setEvaporationSourceError] = useState("");

  // Initialize form when editing or clear it when creating
  useEffect(() => {
    if (stationToEdit) {
      setName(stationToEdit.name || "");
      setBasinId(stationToEdit.basinId || "");
      setPrecipitationRate(stationToEdit.precipitationRate);
      setPrecipitationSourceId(stationToEdit.precipitationSourceId || "");
      setEvaporationRate(stationToEdit.evaporationRate);
      setEvaporationSourceId(stationToEdit.evaporationSourceId || "");
    } else {
      // Reset form for new creation
      setName("");
      setBasinId("");
      setPrecipitationRate(undefined);
      setPrecipitationSourceId("");
      setEvaporationRate(undefined);
      setEvaporationSourceId("");
    }
    // Reset validation errors when opening/closing the form
    resetErrors();
  }, [stationToEdit, isOpen]);

  const resetErrors = () => {
    setNameError("");
    setBasinError("");
    setPrecipitationRateError("");
    setPrecipitationSourceError("");
    setEvaporationRateError("");
    setEvaporationSourceError("");
  };

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError(t("validation.required"));
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate basin
    if (!basinId) {
      setBasinError(t("validation.required"));
      isValid = false;
    } else {
      setBasinError("");
    }

    // Validate precipitation rate
    if (precipitationRate === undefined) {
      setPrecipitationRateError(t("validation.required"));
      isValid = false;
    } else if (precipitationRate < 0) {
      setPrecipitationRateError(t("ipmaStations.validation.positiveNumber"));
      isValid = false;
    } else {
      setPrecipitationRateError("");
    }

    // Validate precipitation source
    if (!precipitationSourceId) {
      setPrecipitationSourceError(t("validation.required"));
      isValid = false;
    } else {
      setPrecipitationSourceError("");
    }

    // Validate evaporation rate
    if (evaporationRate === undefined) {
      setEvaporationRateError(t("validation.required"));
      isValid = false;
    } else if (evaporationRate < 0) {
      setEvaporationRateError(t("ipmaStations.validation.positiveNumber"));
      isValid = false;
    } else {
      setEvaporationRateError("");
    }

    // Validate evaporation source
    if (!evaporationSourceId) {
      setEvaporationSourceError(t("validation.required"));
      isValid = false;
    } else {
      setEvaporationSourceError("");
    }

    return isValid;
  };

  const handlePrecipitationRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setPrecipitationRate(undefined);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setPrecipitationRate(numValue);
    }
  };

  const handleEvaporationRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setEvaporationRate(undefined);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setEvaporationRate(numValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const stationData = {
        name: name.trim(),
        basinId,
        precipitationRate: precipitationRate!,
        precipitationSourceId,
        evaporationRate: evaporationRate!,
        evaporationSourceId,
      };

      if (stationToEdit) {
        // Update existing station
        await FirestoreService.request(
          "edit-ipmaStation",
          { id: stationToEdit.id, ...stationData },
          "setup_ipmaStations",
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("ipmaStations.updated"),
        });
      } else {
        // Create new station
        await FirestoreService.request(
          "create-ipmaStation",
          stationData,
          "setup_ipmaStations",
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("ipmaStations.created"),
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving IPMA station:", error);

      // Handle specific API errors
      if (error.message === "IPMA_STATION_DUPLICATE") {
        toast({
          title: t("error"),
          description: t("ipmaStations.errors.duplicateStation"),
          variant: "destructive",
        });
      } else {
        // Generic error handling
        toast({
          title: t("error"),
          description: stationToEdit ? t("ipmaStations.errors.updateError") : t("ipmaStations.errors.createError"),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out archived information sources for the dropdowns
  const activeInfoSources = infoSources.filter(source => !source.isArchived);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{stationToEdit ? t("ipmaStations.editTitle") : t("ipmaStations.addTitle")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                {t("ipmaStations.name")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                  if (!name.trim()) {
                    setNameError(t("validation.required"));
                  } else {
                    setNameError("");
                  }
                }}
                placeholder={t("ipmaStations.namePlaceholder")}
                className={`placeholder:text-gray-400 placeholder:text-sm ${nameError ? "border-red-300" : ""}`}
                disabled={isLoading}
              />
              {nameError && <p className="text-sm font-medium text-red-500">{nameError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="basin">
                {t("ipmaStations.basin")} <span className="text-red-500">*</span>
              </Label>
              <Select
                value={basinId}
                onValueChange={(value) => {
                  setBasinId(value);
                  setBasinError("");
                }}
                disabled={isLoading}
              >
                <SelectTrigger
                  id="basin"
                  className={`w-full placeholder:text-gray-400 ${basinError ? "border-red-300" : ""}`}
                >
                  <SelectValue placeholder={t("ipmaStations.basinPlaceholder")} />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                  {[...basins]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((basin) => (
                      <SelectItem key={basin.id} value={basin.id}>
                        {basin.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {basinError && <p className="text-sm font-medium text-red-500">{basinError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="precipitationRate">
                {t("ipmaStations.precipitationRate")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="precipitationRate"
                type="number"
                step="any"
                min="0"
                placeholder={t("ipmaStations.precipitationRatePlaceholder")}
                value={precipitationRate ?? ""}
                onChange={handlePrecipitationRateChange}
                onBlur={() => {
                  if (precipitationRate === undefined) {
                    setPrecipitationRateError(t("validation.required"));
                  } else if (precipitationRate < 0) {
                    setPrecipitationRateError(t("ipmaStations.validation.positiveNumber"));
                  } else {
                    setPrecipitationRateError("");
                  }
                }}
                className={`placeholder:text-gray-400 placeholder:text-sm ${precipitationRateError ? "border-red-300" : ""}`}
                disabled={isLoading}
              />
              {precipitationRateError && <p className="text-sm font-medium text-red-500">{precipitationRateError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="precipitationSource">
                {t("ipmaStations.precipitationSource")} <span className="text-red-500">*</span>
              </Label>
              <Select
                value={precipitationSourceId}
                onValueChange={(value) => {
                  setPrecipitationSourceId(value);
                  setPrecipitationSourceError("");
                }}
                disabled={isLoading || activeInfoSources.length === 0}
              >
                <SelectTrigger
                  id="precipitationSource"
                  className={`w-full placeholder:text-gray-400 ${precipitationSourceError ? "border-red-300" : ""}`}
                >
                  <SelectValue placeholder={t("ipmaStations.sourceSelectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {activeInfoSources.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name} ({source.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {precipitationSourceError && <p className="text-sm font-medium text-red-500">{precipitationSourceError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="evaporationRate">
                {t("ipmaStations.evaporationRate")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="evaporationRate"
                type="number"
                step="any"
                min="0"
                placeholder={t("ipmaStations.evaporationRatePlaceholder")}
                value={evaporationRate ?? ""}
                onChange={handleEvaporationRateChange}
                onBlur={() => {
                  if (evaporationRate === undefined) {
                    setEvaporationRateError(t("validation.required"));
                  } else if (evaporationRate < 0) {
                    setEvaporationRateError(t("ipmaStations.validation.positiveNumber"));
                  } else {
                    setEvaporationRateError("");
                  }
                }}
                className={`placeholder:text-gray-400 placeholder:text-sm ${evaporationRateError ? "border-red-300" : ""}`}
                disabled={isLoading}
              />
              {evaporationRateError && <p className="text-sm font-medium text-red-500">{evaporationRateError}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="evaporationSource">
                {t("ipmaStations.evaporationSource")} <span className="text-red-500">*</span>
              </Label>
              <Select
                value={evaporationSourceId}
                onValueChange={(value) => {
                  setEvaporationSourceId(value);
                  setEvaporationSourceError("");
                }}
                disabled={isLoading || activeInfoSources.length === 0}
              >
                <SelectTrigger
                  id="evaporationSource"
                  className={`w-full placeholder:text-gray-400 ${evaporationSourceError ? "border-red-300" : ""}`}
                >
                  <SelectValue placeholder={t("ipmaStations.sourceSelectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {activeInfoSources.map((source) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name} ({source.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {evaporationSourceError && <p className="text-sm font-medium text-red-500">{evaporationSourceError}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={isLoading} className="bg-teal-600 hover:bg-teal-700 text-white">
              {isLoading ? t("saving") : stationToEdit ? t("update") : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 